import React from "react";
import { ActionPlanRiskConsequence, ActionPlanRiskLikelihood, ActionPlanTopic, ActionPlanView, ActionPointData, PublicActionPlanLink } from "../../../amorg-common";
import { deleteActionPlanTopic, updateActionPlanTopic, updateActionPlanTopicRisk } from "../../../api/actionPlanClientApi";
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { TextAvatar } from '../../../designSystem/avatar/TextAvatar';
import { Button } from "../../../designSystem/button/Button";
import { DeleteConfirmationModal } from "../../../designSystem/compositeComponents/DeleteConfirmationModal";
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { H3 } from "../../../designSystem/typography/Heading";
import { AnimatedCollapsibleContent } from "../../../designSystem/collapsible/AnimatedCollapsibleContent";
import { RiskAssessmentEvaluator } from "../../../designSystem/graphs/RiskAssessmentEvaluator";
import { ActionPlanLinkEditor } from "./ActionPlanLinkEditor";
import { ActionPointListPanel } from "../../_components/actionpoints/ActionPointList";
import { Card } from "../../../designSystem/Card";
import { ActionPlanTopicContext } from './ActionPlanTopicContext';
import { ActionplanTopicTitle } from './ActionplanTopicTitle';
import { Divider } from "../../../designSystem/Divider";
import styled from "styled-components";

type ActionPlanTopicListProps = {
    siteId:string,
    actionPlan: ActionPlanView,
    links: PublicActionPlanLink[],
    onUpdated: () => void;
};
export function ActionPlanTopicList (props: ActionPlanTopicListProps): JSX.Element
{
    return (
        <>
            {props.actionPlan.topics.length > 0 &&
                <Flex flexDirection="column" gap={16}>
                    {props.actionPlan.topics.map((t, i) =>
                    {
                        return (

                            <ActionPlanTopicView key={t.id}
                                siteId={props.siteId}
                                activityId={props.actionPlan.activityId}
                                actionPlanId={props.actionPlan.id}
                                topicIndex={i}
                                topic={t}
                                links={props.links}
                                onUpdated={props.onUpdated} />
                        );
                    })}
                </Flex>
            }
        </>
    );
}

type ActionPlanTopicViewProps =
    {
        siteId:string,
        activityId:string,
        actionPlanId:string,
        topicIndex: number,
        topic: ActionPlanTopic,
        links: PublicActionPlanLink[],
        onUpdated: () => void,
    };
function ActionPlanTopicView (props: ActionPlanTopicViewProps): JSX.Element
{
    const [isOpen, setIsOpen] = React.useState(false);
    const [inEditActionPoint, setInEditActionPoint] = React.useState<ActionPointData | null>(null);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    // Refresh inEditActionPoint actionPoints are updated
    React.useEffect(() =>
    {
        if (inEditActionPoint)
        {
            const selected = props.topic.actionPoints.find(ap => ap.id === inEditActionPoint.id) || null;
            setInEditActionPoint(selected);
        }
        setIsOpen(true);
    }, [props.topic.actionPoints]);

    async function handleSaveDescription (value: string)
    {
        if ((await updateActionPlanTopic(props.topic.id, value)).isSuccess)
            props.onUpdated();
    }

    async function handleClickDeleteTopic ()
    {
        if ((await deleteActionPlanTopic(props.topic.id)).isSuccess)
        {
            props.onUpdated();
        }
        setShowDeleteModal(false);
    }

    async function handleUpdateTopicRisk (consequence: ActionPlanRiskConsequence, likelihood: ActionPlanRiskLikelihood)
    {
        setLoading(true);
        await updateActionPlanTopicRisk(props.topic.id, consequence, likelihood);
        setLoading(false);
        props.onUpdated();
    }

    return (
        <>
            <Card variant="outlined" padding="8px">
                <LoadingPageOverlay loading={loading} />

                <Flex flexDirection="column" gap={8}>
        
                <StyledTopicInlineSizeWrapper>
                <StyledTopicContainer>

                    <StyledAvatar>
                        <Flex alignItems="center" gap={4} style={{minWidth: '80px'}}>
                            <Button variant="text" 
                                icon={isOpen ? 'arrows_chevron_chevronUp' : 'arrows_chevron_chevronDown'} 
                                onClick={() => setIsOpen(!isOpen)} />

                            <div>
                                <TextAvatar text={`${props.topicIndex + 1}`} size="32px" />
                            </div>
                        </Flex>
                    </StyledAvatar>

                    <StyledTitle>
                        <ActionplanTopicTitle topic={props.topic} onSaveDescription={handleSaveDescription} />
                    </StyledTitle>
                    
                    <StyledRisk>
                        <Card variant="outlined" padding="4px" style={{flex: '1 1 auto'}}>
                            <RiskAssessmentEvaluator
                                consequence={props.topic.risk.consequence}
                                likelihood={props.topic.risk.likelihood} 
                                onChange={handleUpdateTopicRisk}
                            />
                        </Card>
                    </StyledRisk>
                        
                    <StyledActions>
                            <ActionPlanLinkEditor
                                siteId={props.siteId}
                                actionPlanId={props.actionPlanId}
                                linkContext={{ type:'actionPlanTopic', topicId: props.topic.id }}
                                link={props.links.find(l => l.configuration.type === 'actionPlanTopic' && l.configuration.topicId === props.topic.id)}
                                onUpdated={props.onUpdated}  />
                            <Button variant="destructive-text" icon="interface_actions_delete" onClick={() => setShowDeleteModal(true)} />
                    </StyledActions>

                    {props.topic.type === 'WPA' &&
                        <StyledContext>
                            <Card variant="lightGray" style={{height:'100%'}} padding="8px">
                                <ActionPlanTopicContext topic={props.topic} small={true} />
                            </Card>
                        </StyledContext>
                    }
                </StyledTopicContainer>
                </StyledTopicInlineSizeWrapper>

                <Divider />

                <AnimatedCollapsibleContent open={isOpen}>
                    <div>
                        <H3 style={{margin:'8px 16px'}}>Handlepunkter</H3>
                        <ActionPointListPanel
                            mode={{ type: 'actionPlanTopic', topicId: props.topic.id }}
                            actionPoints={props.topic.actionPoints}
                            linkDisplay={{
                                siteId: props.siteId,
                                actionPlanId: props.actionPlanId,
                                links: props.links
                            }}
                            onUpdated={props.onUpdated} 
                        />
                    </div>
                </AnimatedCollapsibleContent>
                </Flex>
            </Card>

            <DeleteConfirmationModal
                showModal={showDeleteModal}
                confirmationHeading="Slet emne?"
                confirmationText='Er du sikker på du ønsker at slette dette emne?'
                requireKey={false}
                onConfirmDelete={handleClickDeleteTopic}
                buttonText="Slet emne"
                onCancel={() => setShowDeleteModal(false)} />
        </>
    );
}

const breakpoint = '900px';

// Used to make container responsive based on parent rather than viewport width
const StyledTopicInlineSizeWrapper = styled.div`
    container-type: inline-size;
    container-name: topic;
`;

const StyledTopicContainer = styled.div`
    display: grid;
    grid-template-columns: 80px minmax(0, 1fr) auto 100px;
    grid-template-areas:
        "avatar title risk actions"
        "context context context context";
    gap: 8px;
    min-width: 0;
    flex: 1 1 auto;

    @container topic (max-width: ${breakpoint}) {
        grid-template-columns: minmax(0, 1fr) 80px;
        grid-template-areas:
            "title actions"
            "risk risk"
            "context context";
    }
`;

const StyledAvatar = styled.div`
    grid-area: avatar;

    @container topic (max-width: ${breakpoint}) {
        display: none;
    }
`;

const StyledTitle = styled.div`
    grid-area: title;
`;

const StyledRisk = styled.div`
    grid-area: risk;
`;

const StyledActions = styled.div`
    grid-area: actions;
`;

const StyledContext = styled.div`
    grid-area: context;
`;